import type { LensCardData, LensFilterValues } from '~/types/leLens';

import { num, ratio } from './cards';

export const DAILY_SNAPSHOT_PAGE_ID = 'daily-snapshot';

export const SNAPSHOT_FILTERS = [
  { key: 'offer_continent', label: 'Offer continent' },
  { key: 'offer_country', label: 'Offer country' },
] as const;
type SnapshotFilterKey = (typeof SNAPSHOT_FILTERS)[number]['key'];
export type SnapshotFilters = Record<SnapshotFilterKey, Array<string>>;

export function parseSnapshotFilters(search: URLSearchParams): SnapshotFilters {
  return Object.fromEntries(SNAPSHOT_FILTERS.map(({ key }) => [key, search.getAll(key)])) as SnapshotFilters;
}

export function writeSnapshotFilters(filters: SnapshotFilters, search: URLSearchParams): URLSearchParams {
  const next = new URLSearchParams(search);
  for (const { key } of SNAPSHOT_FILTERS) {
    next.delete(key);
    filters[key].forEach((value) => next.append(key, value));
  }
  return next;
}

/** No period is sent: every card is pinned to its own window relative to yesterday in Sydney. */
export function snapshotRequest(filters: SnapshotFilters): LensFilterValues {
  return Object.fromEntries(
    SNAPSHOT_FILTERS.filter(({ key }) => filters[key].length).map(({ key }) => [key, filters[key]]),
  );
}

type Totals = { grossSales: number | null; grossMargin: number | null; grossSalesAfterRefunds: number | null };

function totalsOf(card: LensCardData | undefined, periodId: string): Totals {
  const row = card?.periods?.find((period) => period.id === periodId)?.result.rows?.[0] ?? {};
  return {
    grossSales: num(row.gross_sales_br_aud),
    grossMargin: num(row.gross_margin_ar_aud),
    grossSalesAfterRefunds: num(row.gross_sales_ar_aud),
  };
}

const addDays = (day: string, days: number): string => {
  const date = new Date(`${day}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
};

const lastDayOfMonth = (day: string): string => {
  const date = new Date(`${day}T00:00:00Z`);
  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + 1, 0)).toISOString().slice(0, 10);
};

const daysBetween = (from: string, to: string): number =>
  Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000);

type SnapshotKpis = {
  yesterday: string;
  today: string;
  daysUntilEndOfMonth: number;
  grossSales: number | null;
  grossMargin: number | null;
  grossMarginRate: number | null;
  mtdGrossSales: number | null;
  mtdGrossMargin: number | null;
  romGrossSales: number | null;
  romGrossMargin: number | null;
  eomGrossSales: number | null;
  eomGrossMargin: number | null;
};

/**
 * The workbook's TODAY()-based tiles, from yesterday, month-to-date and trailing-21-day sums.
 *
 * - Days until end of month = last day of today's month − today + 1.
 * - Rest of month (ROM) forecast = (sum over the 21 days to yesterday ÷ 21) × days until end of month,
 *   and 0 on the last day of the month (the workbook stops projecting once the last sale date is the month end).
 * - End of month (EOM) forecast = MTD + ROM; on the 1st, yesterday's month has closed, so it is ROM alone.
 * - Gross Margin (AR) % = Gross Margin (AR) ÷ Gross Sales (AR), both for yesterday.
 */
export function snapshotKpis(card: LensCardData | undefined, yesterday: string): SnapshotKpis {
  const day = totalsOf(card, 'yesterday');
  const mtd = totalsOf(card, 'mtd');
  const last21 = totalsOf(card, 'last21');
  const today = addDays(yesterday, 1);
  const monthEnd = lastDayOfMonth(today);
  const daysUntilEndOfMonth = daysBetween(today, monthEnd) + 1;
  const project = (sum: number | null) =>
    sum === null ? null : today === monthEnd ? 0 : (sum / 21) * daysUntilEndOfMonth;
  const romGrossSales = project(last21.grossSales);
  const romGrossMargin = project(last21.grossMargin);
  const newMonth = today.slice(0, 7) !== yesterday.slice(0, 7);
  const endOfMonth = (monthToDate: number | null, rest: number | null) =>
    rest === null ? null : newMonth ? rest : monthToDate === null ? null : monthToDate + rest;
  return {
    yesterday,
    today,
    daysUntilEndOfMonth,
    grossSales: day.grossSales,
    grossMargin: day.grossMargin,
    grossMarginRate: ratio(day.grossMargin, day.grossSalesAfterRefunds),
    mtdGrossSales: mtd.grossSales,
    mtdGrossMargin: mtd.grossMargin,
    romGrossSales,
    romGrossMargin,
    eomGrossSales: endOfMonth(mtd.grossSales, romGrossSales),
    eomGrossMargin: endOfMonth(mtd.grossMargin, romGrossMargin),
  };
}
